import { useEffect, useState } from 'react'
import { useAuthHeaders } from '@hooks/useAuthHeaders'
import { serverUrl } from '@utils/MkEnv'
import { COUNTRIES_API, getStatesApi } from '@account/services/MkAccountConstants'

const COUNTRY_CACHE_KEY = 'countries'
const CACHE_EXPIRATION_MS = 24 * 60 * 60 * 1000 // 1 day

export function useCountryStateOptions(selectedCountry: string) {
  const [countries, setCountries] = useState<any[]>([])
  const [states, setStates] = useState<any[]>([])
  const authHeaders = useAuthHeaders()
  const accessCode = authHeaders['Access-Code']

  // Fetch countries once (with caching)
  useEffect(() => {
    async function fetchCountries() {
      try {
        if (accessCode) {
          // Try reading from cache
          const cached = localStorage.getItem(COUNTRY_CACHE_KEY)
          if (cached) {
            const parsed = JSON.parse(cached)
            const isExpired = Date.now() - parsed.timestamp > CACHE_EXPIRATION_MS
            if (!isExpired && Array.isArray(parsed.data)) {
              setCountries(parsed.data)
              return // Use cache, skip fetch
            }
          }

          // Otherwise fetch from API
          if (authHeaders['Access-Code']) {
            const res = await fetch(`${serverUrl}${COUNTRIES_API}`, { headers: authHeaders })
            if (!res.ok) {
              setCountries([])
              return
            }
            const data = await res.json()
            if (Array.isArray(data)) {
              setCountries(data)
              localStorage.setItem(
                COUNTRY_CACHE_KEY,
                JSON.stringify({
                  data,
                  timestamp: Date.now(),
                }),
              )
            } else {
              setCountries([])
            }
          }
        }
      } catch (err: any) {
        if (err?.name !== 'AbortError') {
          setCountries([])
        }
      }
    }
    fetchCountries()
  }, [accessCode])

  // Fetch states when selectedCountry changes
  useEffect(() => {
    if (!selectedCountry) {
      setStates([])
      return
    }

    async function fetchStates() {
      try {
        if (accessCode && selectedCountry && selectedCountry !== '') {
          const res = await fetch(`${serverUrl}${getStatesApi(selectedCountry)}`, { headers: authHeaders })
          if (!res.ok) {
            setStates([])
            return
          }
          const data = await res.json()
          setStates(Array.isArray(data) ? data : [])
        }
      } catch (err: any) {
        if (err?.name !== 'AbortError') {
          setStates([])
        }
      }
    }
    fetchStates()
  }, [selectedCountry, accessCode])

  return { countries, states }
}
